interface FeKeyInterceptorMutationObserver extends MutationObserver {
  feKeyInterceptor: FeKeyInterceptor;
}
interface FeKeyInterceptorHTMLElement extends HTMLElement {
  feKeyInterceptor: FeKeyInterceptor;
}

/**
 * Factory class to manage the connection, update, and disconnection of FeKeyInterceptor instances.
 */
export default class FeKeyInterceptorFactory {

  /**
   * Connects a FeKeyInterceptor to an HTML element.
   * @param dotNetRef - Reference to the .NET object.
   * @param elementId - ID of the HTML element to connect to.
   * @param options - Configuration options for the interceptor.
   */
  connect(dotNetRef: any, elementId: string, options: any): void {
    if (!elementId)
      throw "elementId: expected element id!";

    var element = document.getElementById(elementId) as FeKeyInterceptorHTMLElement;
    if (!element)
      throw "no element found for id: " + elementId;

    if (!element.feKeyInterceptor)
      element.feKeyInterceptor = new FeKeyInterceptor(dotNetRef, options);

    element.feKeyInterceptor.connect(element);
  }

  /**
   * Updates the key options for an existing FeKeyInterceptor.
   * @param elementId - ID of the HTML element.
   * @param option - New key option to update.
   */
  updateKey(elementId: string, option: any): void {
    var element = document.getElementById(elementId) as FeKeyInterceptorHTMLElement;
    if (!element || !element.feKeyInterceptor)
      return;

    element.feKeyInterceptor.updatekey(option);
  }

  /**
   * Disconnects the FeKeyInterceptor from an HTML element.
   * @param elementId - ID of the HTML element.
   */
  disconnect(elementId: string): void {
    var element = document.getElementById(elementId) as FeKeyInterceptorHTMLElement;
    if (!element || !element.feKeyInterceptor)
      return;

    element.feKeyInterceptor.disconnect();
  }
}

/**
 * Class to intercept and handle key events on an HTML element.
 */
class FeKeyInterceptor {
  private _dotNetRef: any;
  private _options: any;
  private logger: (message?: any, ...optionalParams: any[]) => void;
  private _observer: FeKeyInterceptorMutationObserver | null = null;
  private _element: HTMLElement | null = null;
  private _observedChildren: HTMLElement[] = [];
  private _keyOptions: { [key: string]: any } = {};
  private _regexOptions: any[] = [];

  constructor(dotNetRef: any, options: any) {
    this._dotNetRef = dotNetRef;
    this._options = options;
    this.logger = options.enableLogging ? console.log : (message) => { };
    this.logger('[FeBlazorLibrary | KeyInterceptor] Interceptor initialized', { options });
  }

  /**
   * Connects the interceptor to an HTML element and starts observing for DOM changes.
   * @param element - The HTML element to connect to.
   */
  connect(element: HTMLElement): void {
    if (!this._options)
      return;
    if (!this._options.keys)
      throw "_options.keys: array of KeyOptions expected";
    if (!this._options.targetClass)
      throw "_options.targetClass: css class name expected";
    if (this._observer) {
      // Don't do double registration
      return;
    }
    var targetClass = this._options.targetClass;
    this.logger('[FeBlazorLibrary | KeyInterceptor] Start observing DOM of element for changes to child with class ', { element, targetClass });
    this._element = element;
    this._observer = new MutationObserver(this.onDomChanged) as FeKeyInterceptorMutationObserver;
    this._observer.feKeyInterceptor = this;
    this._observer.observe(this._element, { attributes: false, childList: true, subtree: true });
    this._observedChildren = [];
    // Transform key options into a key lookup
    this._keyOptions = {};
    this._regexOptions = [];
    for (const keyOption of this._options.keys) {
      if (!keyOption || !keyOption.key) {
        this.logger('[FeBlazorLibrary | KeyInterceptor] got invalid key options: ', keyOption);
        continue;
      }
      this.setKeyOption(keyOption)
    }
    this.logger('[FeBlazorLibrary | KeyInterceptor] key options: ', this._keyOptions);
    if (this._regexOptions.length > 0)
      this.logger('[FeBlazorLibrary | KeyInterceptor] regex options: ', this._regexOptions);
    // Register handlers
    for (const child of this._element.getElementsByClassName(targetClass)) {
      this.attachHandlers(child as FeKeyInterceptorHTMLElement);
    }
  }

  /**
   * Sets the key options for the interceptor.
   * @param keyOption - The key option to set.
   */
  setKeyOption(keyOption: any): void {
    if (keyOption.key.length > 2 && keyOption.key.startsWith('/') && keyOption.key.endsWith('/')) {
      // JS regex key options such as "/[a-z]/" or "/a|b/" but NOT "/[a-z]/g" or "/[a-z]/i"
      keyOption.regex = new RegExp(keyOption.key.substring(1, keyOption.key.length - 1)); // Strip the / from start and end
      this._regexOptions.push(keyOption);
    } else {
      this._keyOptions[keyOption.key.toLowerCase()] = keyOption;
    }
    // Remove whitespace and enforce lowercase
    var whitespace = new RegExp("\\s", "g");
    keyOption.preventDown = (keyOption.preventDown || "none").replace(whitespace, "").toLowerCase();
    keyOption.preventUp = (keyOption.preventUp || "none").replace(whitespace, "").toLowerCase();
    keyOption.stopDown = (keyOption.stopDown || "none").replace(whitespace, "").toLowerCase();
    keyOption.stopUp = (keyOption.stopUp || "none").replace(whitespace, "").toLowerCase();
  }

  /**
   * Updates the key options for the interceptor.
   * @param updatedOption - The updated key option.
   */
  updatekey(updatedOption: any): void {
    var option = this._keyOptions[updatedOption.key.toLowerCase()];
    if (!option) {
      this.logger('[FeBlazorLibrary | KeyInterceptor] updating option failed: key not registered');
      return;
    }
    this.setKeyOption(updatedOption);
    this.logger('[FeBlazorLibrary | KeyInterceptor] updated option ', { option, updatedOption });
  }

  /**
   * Disconnects the interceptor and removes all event handlers.
   */
  disconnect(): void {
    if (!this._observer)
      return;
    this.logger('[FeBlazorLibrary | KeyInterceptor] disconnect mutation observer and event handlers');
    this._observer.disconnect();
    this._observer = null;
    for (const child of this._observedChildren)
      this.detachHandlers(child);
  }

  /**
   * Attaches key event handlers to a child element.
   * @param child - The child element to attach handlers to.
   */
  attachHandlers(child: FeKeyInterceptorHTMLElement): void {
    this.logger('[FeBlazorLibrary | KeyInterceptor] attaching handlers ', { child });
    if (this._observedChildren.indexOf(child) > -1) {
      // Handlers already attached
      return;
    }
    child.feKeyInterceptor = this;
    child.addEventListener('keydown', this.onKeyDown);
    child.addEventListener('keyup', this.onKeyUp);
    this._observedChildren.push(child);
  }

  /**
   * Detaches key event handlers from a child element.
   * @param child - The child element to detach handlers from.
   */
  detachHandlers(child: HTMLElement): void {
    this.logger('[FeBlazorLibrary | KeyInterceptor] detaching handlers ', { child });
    child.removeEventListener('keydown', this.onKeyDown);
    child.removeEventListener('keyup', this.onKeyUp);
    this._observedChildren = this._observedChildren.filter(x => x !== child);
  }

  /**
   * Callback for DOM changes observed by MutationObserver.
   * @param mutationsList - List of mutations observed.
   * @param observer - The MutationObserver instance.
   */
  onDomChanged(mutationsList: MutationRecord[], observer: MutationObserver): void {
    //@ts-ignore
    var self = this.feKeyInterceptor; // Function is invoked with this == _observer
    var targetClass = self._options.targetClass;
    for (const mutation of mutationsList) {
      for (const element of mutation.addedNodes) {
        if ((element as HTMLElement).classList && (element as HTMLElement).classList.contains(targetClass))
          self.attachHandlers(element as HTMLElement);
      }
      for (const element of mutation.removedNodes) {
        if ((element as HTMLElement).classList && (element as HTMLElement).classList.contains(targetClass))
          self.detachHandlers(element as HTMLElement);
      }
    }
  }

  /**
   * Checks if the key combination matches the specified option.
   * @param option - The key combination option.
   * @param args - The keyboard event arguments.
   * @returns True if the combination matches, otherwise false.
   */
  matchesKeyCombination(option: string, args: KeyboardEvent): boolean {
    if (!option || option === "none")
      return false;
    if (option === "any")
      return true;
    var shift = args.shiftKey;
    var ctrl = args.ctrlKey;
    var alt = args.altKey;
    var meta = args.metaKey;
    var any = shift || ctrl || alt || meta;
    if (any && option === "key+any")
      return true;
    if (!any && option.includes("key+none"))
      return true;
    if (!any)
      return false;
    var combi = `key${shift ? "+shift" : ""}${ctrl ? "+ctrl" : ""}${alt ? "+alt" : ""}${meta ? "+meta" : ""}`;
    return option.includes(combi);
  }

  /**
   * Handles the keydown event.
   * @param args - The keyboard event arguments.
   */
  onKeyDown(args: KeyboardEvent): void {
    //@ts-ignore
    var self = this.feKeyInterceptor; // Function is invoked with this == child
    if (!args.key) {
      self.logger('[FeBlazorLibrary | KeyInterceptor] key is undefined', args);
      return;
    }

    var key = args.key.toLowerCase();
    self.logger('[FeBlazorLibrary | KeyInterceptor] down "' + key + '"', args);
    var invoke = false;
    if (self._keyOptions.hasOwnProperty(key)) {
      var keyOptions = self._keyOptions[key];
      self.logger('[FeBlazorLibrary | KeyInterceptor] options for "' + key + '"', keyOptions);
      self.processKeyDown(args, keyOptions);
      if (keyOptions.subscribeDown)
        invoke = true;
    }
    for (const keyOptions of self._regexOptions) {
      if (keyOptions.regex.test(key)) {
        self.logger('[FeBlazorLibrary | KeyInterceptor] regex options for "' + key + '"', keyOptions);
        self.processKeyDown(args, keyOptions);
        if (keyOptions.subscribeDown)
          invoke = true;
      }
    }
    if (invoke) {
      var eventArgs = self.toKeyboardEventArgs(args);
      eventArgs.Type = "keydown";
      self._dotNetRef.invokeMethodAsync('OnKeyDown', self._element!.id, eventArgs);
    }
  }

  /**
   * Processes the keydown event based on the key options.
   * @param args - The keyboard event arguments.
   * @param keyOptions - The key options to process.
   */
  processKeyDown(args: KeyboardEvent, keyOptions: any): void {
    if (this.matchesKeyCombination(keyOptions.preventDown, args))
      args.preventDefault();
    if (this.matchesKeyCombination(keyOptions.stopDown, args))
      args.stopPropagation();
  }

  /**
   * Handles the keyup event.
   * @param args - The keyboard event arguments.
   */
  onKeyUp(args: KeyboardEvent): void {
    //@ts-ignore
    var self = this.feKeyInterceptor; // Function is invoked with this == child
    if (!args.key) {
      self.logger('[FeBlazorLibrary | KeyInterceptor] key is undefined', args);
      return;
    }

    var key = args.key.toLowerCase();
    self.logger('[FeBlazorLibrary | KeyInterceptor] up "' + key + '"', args);
    var invoke = false;
    if (self._keyOptions.hasOwnProperty(key)) {
      var keyOptions = self._keyOptions[key];
      self.processKeyUp(args, keyOptions);
      if (keyOptions.subscribeUp)
        invoke = true;
    }
    for (const keyOptions of self._regexOptions) {
      if (keyOptions.regex.test(key)) {
        self.processKeyUp(args, keyOptions);
        if (keyOptions.subscribeUp)
          invoke = true;
      }
    }
    if (invoke) {
      var eventArgs = self.toKeyboardEventArgs(args);
      eventArgs.Type = "keyup";
      self._dotNetRef.invokeMethodAsync('OnKeyUp', self._element!.id, eventArgs);
    }
  }

  /**
   * Processes the keyup event based on the key options.
   * @param args - The keyboard event arguments.
   * @param keyOptions - The key options to process.
   */
  processKeyUp(args: KeyboardEvent, keyOptions: any): void {
    if (this.matchesKeyCombination(keyOptions.preventUp, args))
      args.preventDefault();
    if (this.matchesKeyCombination(keyOptions.stopUp, args))
      args.stopPropagation();
  }

  /**
   * Converts keyboard event arguments to a format suitable for .NET interop.
   * @param args - The keyboard event arguments.
   * @returns The converted keyboard event arguments.
   */
  toKeyboardEventArgs(args: KeyboardEvent): any {
    return {
      Key: args.key,
      Code: args.code,
      Location: args.location,
      Repeat: args.repeat,
      CtrlKey: args.ctrlKey,
      ShiftKey: args.shiftKey,
      AltKey: args.altKey,
      MetaKey: args.metaKey
    };
  }
}
